<script setup lang="ts">
   import { computed, onMounted, ref } from 'vue';
   import axios from 'axios';
   import { definePageMeta, useHead, useRuntimeConfig } from '#imports';
   import { formatDateString } from '~/functions/parseDateString';
   import {
      entryCloseDateLabel,
      entryStatusBadge,
      isClosedForEntry,
      isOpenForEntry,
      showPaymentLink,
      tournamentEntryRoute,
   } from '~/functions/tournamentEntry';
   import TournamentPaymentLink from '~/components/tournaments/TournamentPaymentLink.vue';
   import type { Tournament, TournamentsResponse } from '~/types/types';

   definePageMeta({
      layout: 'wow-polo',
   });

   useHead({
      title: 'WOW Polo Series — Women of Wellington Polo Series',
   });

   const config = useRuntimeConfig();
   const tournaments = ref<Tournament[]>([]);
   const isLoading = ref(true);
   const loadError = ref(false);

   const fetchTournaments = async () => {
      try {
         const response = await axios.get<TournamentsResponse>(
            `${config.public.lmsApiBaseurl}public/tournaments`,
            {
               params: {
                  brand: config.public.portalBrand,
                  // No sort override: the endpoint's default is upcoming ascending, then
                  // past descending, which is what this section wants. Sorting by
                  // startDate ascending put the oldest tournaments first and, once the
                  // brand passes per_page, truncated away the current season - this page
                  // has no paginator, so whatever falls off the end is simply gone.
                  per_page: 50,
               },
               headers: { 'X-LMS-Requester': config.public.siteDomain as string },
            }
         );
         tournaments.value = response.data.data;
      } catch (e) {
         console.error('Failed to load WOW Polo tournaments', e);
         loadError.value = true;
      } finally {
         isLoading.value = false;
      }
   };

   onMounted(fetchTournaments);

   const formatRange = (start: string | null, end: string | null) => {
      if (!start && !end) return '';
      if (start && end) return `${formatDateString(start)} – ${formatDateString(end)}`;
      return formatDateString(start || end);
   };

   const hasOpenEntries = computed(() => tournaments.value.some(isOpenForEntry));

   // A tournament whose registration has closed but whose entry fee is still payable has
   // to keep its card, otherwise the section disappears the moment the last registration
   // closes and takes the only remaining route to the payment page with it.
   const hasPayableTournaments = computed(() => tournaments.value.some((t) => showPaymentLink(t)));
</script>

<template>
   <div>
      <!-- Hero -->
      <section class="wow-hero-bg relative h-[520px] bg-cover bg-center bg-no-repeat">
         <div class="absolute inset-0 bg-black/50" />
         <div class="relative flex h-full flex-col items-center justify-center px-6 text-center">
            <!-- Two lines on mobile, one on desktop. Flex rather than a hidden <br> so the
                 word gap comes from gap-x-1 and does not depend on template whitespace. -->
            <p class="mb-3 flex flex-col items-center justify-center gap-x-1 text-sm font-semibold uppercase tracking-widest text-white/80 md:flex-row">
               <span>Presented by</span>
               <span>The Brennan Family Foundation</span>
            </p>
            <h1 class="mb-4 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
               Women of Wellington Polo Series
            </h1>
            <p class="mb-8 max-w-xl text-lg text-white/90">
               Empowering Women, Elevating Polo, Changing the Game
            </p>
            <div class="flex gap-4">
               <NuxtLink
                  to="/tournaments"
                  class="wow-btn-primary rounded-full px-8 py-3 font-semibold text-white transition-opacity hover:opacity-90">
                  View Tournaments
               </NuxtLink>
               <NuxtLink
                  to="/contact"
                  class="rounded-full border-2 border-white px-8 py-3 font-semibold text-white transition-colors hover:bg-white hover:text-gray-900">
                  Contact Us
               </NuxtLink>
            </div>
         </div>
      </section>

      <!-- About -->
      <section class="bg-white py-16">
         <WowPoloSectionHeading title="About WOW Polo" />

         <div class="mx-auto mt-12 max-w-6xl gap-12 px-6 lg:grid lg:grid-cols-[3fr_1fr]">
            <div class="space-y-5 leading-relaxed text-gray-700">
               <p>
                  The Women of Wellington (WOW) medium-goal polo league offers more options to female players, the strongest segment of growth within the USPA, to compete in organized polo in a location with a strong polo presence. WOW will provide playing opportunities and a platform to promote amateur and professional women players to encourage greater participation in the U.S. Open Women's Polo Championship.
               </p>
               <p>
                  The formation of the league aims to enable young, new and otherwise unincorporated female players to participate in organized tournaments with USPA's professional umpires on quality fields. The league will provide valuable playing experience to team owners/amateurs and job opportunities to a large pool of available higher-rated women players.
               </p>
               <p>
                  WOW looks to inspire players to form teams and enter prestigious tournaments by connecting new and seasoned players on the field. Experience in medium goal will be highly valuable preparing female members for premier women's tournament play in the United States and beyond.
               </p>
            </div>
            <div class="mt-8 flex justify-center lg:mt-0">
               <img
                  src="/img/wow-polo-logo.webp"
                  alt="WOW Polo Series"
                  class="size-48 rounded-full object-cover" />
            </div>
         </div>
      </section>

      <!-- Tournaments -->
      <section
         v-if="isLoading || loadError || hasOpenEntries || hasPayableTournaments"
         id="tournaments"
         class="wow-scroll-offset bg-gray-50 py-16">
         <WowPoloSectionHeading title="Tournaments" />

         <div v-if="isLoading" class="mx-auto mt-12 grid max-w-6xl gap-8 px-6 md:grid-cols-3">
            <div
               v-for="n in 3"
               :key="n"
               class="h-56 animate-pulse rounded-xl border border-gray-100 bg-white p-8 shadow-sm" />
         </div>

         <div v-else-if="loadError" class="mx-auto mt-12 max-w-2xl px-6 text-center text-gray-600">
            We couldn't load the tournament schedule right now. Please try again later.
         </div>

         <div v-else class="mx-auto mt-12 grid max-w-6xl gap-8 px-6 md:grid-cols-3">
            <div
               v-for="tournament in tournaments"
               :key="tournament.id"
               class="rounded-xl border border-gray-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
               <div class="wow-icon-circle mb-4 flex size-12 items-center justify-center rounded-full">
                  <img src="/img/icons/trophy.svg" alt="" class="size-6 invert" />
               </div>

               <h3 class="mb-2 text-lg font-bold text-gray-900">{{ tournament.tournament }}</h3>
               <p class="mb-3 text-sm text-gray-600">{{ formatRange(tournament.startDate, tournament.endDate) }}</p>

               <span
                  v-if="entryStatusBadge(tournament)"
                  class="mb-4 inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
                  :class="entryStatusBadge(tournament)!.tone === 'open'
                     ? 'bg-green-100 text-green-700'
                     : 'bg-red-100 text-red-700'">
                  {{ entryStatusBadge(tournament)!.label }}
               </span>

               <div v-if="isOpenForEntry(tournament)" class="mt-4">
                  <NuxtLink
                     :to="tournamentEntryRoute(tournament)"
                     class="wow-btn-primary block rounded-md px-4 py-2 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90">
                     Tournament Entry
                  </NuxtLink>
                  <p v-if="entryCloseDateLabel(tournament)" class="mt-2 text-center text-xs text-gray-500">
                     Closes {{ entryCloseDateLabel(tournament) }}
                  </p>
               </div>

               <p v-else-if="isClosedForEntry(tournament)" class="mt-4 text-center text-sm font-medium text-gray-500">
                  Registration closed
               </p>

               <TournamentPaymentLink :tournament="tournament" class="mt-3" />
            </div>
         </div>
      </section>

      <!-- Partners -->
      <section id="partners" class="wow-scroll-offset bg-white py-16">
         <WowPoloSectionHeading title="Our Partners" />
         <WowPoloPartners />
      </section>
   </div>
</template>

<style scoped>
   .wow-hero-bg {
      background-image: url('/img/home-page-hero-bg.webp');
   }

   .wow-btn-primary {
      background-color: #5cc8d9;
   }

   .wow-icon-circle {
      background-color: #7cdfef;
   }

   .wow-scroll-offset {
      scroll-margin-top: 6rem;
   }
</style>
